import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row, Col, Card, Table, Button, Badge, Form, Nav, Modal } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import Pager from './Pager';
import IngresoModal, { money, METHOD_LABEL } from './IngresoModal';
import BankAccountsModal from './BankAccountsModal';
import TransportesTab from './TransportesTab';
import { formatDay, formatDateTime } from '../utils/dates';
import Loading from './Loading';

const PAGE_SIZE = 10;
const TABS = ['pending', 'verify', 'credits', 'history', 'transport'];
// Accent- and case-insensitive, so "jose" finds "José".
const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
// A due date is a bare date (midnight UTC): show it as written. A timestamp is shown on the Dominican calendar.
const dueDay = (d) => formatDay(d);
const stamp = (d) => formatDay(d);
const stampTime = (d) => formatDateTime(d);

// The payment rows come one per invoice / advance / use of saldo a favor; an ingreso is all the rows sharing a `receipt`
// (an older single payment has none and is its own ingreso). Everything below works on these groups.
const groupPayments = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const key = r.receipt || r._id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return [...map.entries()].map(([key, list]) => {
    const first = list[0];
    const perInvoice = new Map();
    let advance = 0; let creditUsed = 0; let cash = 0;
    for (const r of list) {
      if (r.kind === 'transport') { cash += r.amount; continue; }
      if (r.kind === 'advance') advance += r.amount;
      else perInvoice.set(r.invoice?.number || '?', (perInvoice.get(r.invoice?.number || '?') || 0) + r.amount);
      if (r.kind === 'application') creditUsed += r.amount; else cash += r.amount;
    }
    const moneyRow = list.find(r => r.kind !== 'application');
    return {
      key, id: first._id, transport: first.kind === 'transport', status: first.status || 'verified', date: first.date, createdAt: first.createdAt, createdBy: first.createdBy, notes: first.notes,
      client: first.client?.name || first.invoice?.client?.name || '—',
      cash, advance, creditUsed, invoices: [...perInvoice], method: moneyRow?.method, accountName: moneyRow?.accountName,
      verifiedBy: first.verifiedBy, rejectedBy: first.rejectedBy, rejectionReason: first.rejectionReason, voidedBy: first.voidedBy, voidReason: first.voidReason,
    };
  });
};

// What an ingreso was applied to: the invoices, any part left as anticipo, any saldo a favor it used.
const AppliedTo = ({ g }) => (
  <div className="d-flex flex-wrap gap-1">
    {g.transport && <Badge bg="warning" text="dark" className="fw-normal" title="Cobrado sin factura (Ingresos → Transportes)"><i className="bi bi-truck me-1" />Transporte</Badge>}
    {g.invoices.map(([n, a]) => <Badge key={n} bg="secondary" className="fw-normal">{n} · {money(a)}</Badge>)}
    {g.advance > 0 && <Badge bg="info" className="fw-normal" title="Quedó como saldo a favor del cliente">Anticipo · {money(g.advance)}</Badge>}
    {g.creditUsed > 0 && <Badge bg="dark" className="fw-normal" title="Se pagó con saldo a favor del cliente (no es dinero nuevo)">Saldo a favor · {money(g.creditUsed)}</Badge>}
  </div>
);

const describe = (g) => `${g.cash > 0 ? `Ingreso de ${money(g.cash)}` : `Uso de saldo a favor (${money(g.creditUsed)})`} de ${g.client}`;

// Where an ingreso stands: por verificar (recorded by sales, not counted yet), verified (counts), denied or voided.
const StatusBadge = ({ g }) => {
  if (g.status === 'pending') return <Badge bg="warning" title="Un administrador todavía no lo aprueba: no cuenta como recibido">Por verificar</Badge>;
  if (g.status === 'rejected') return <Badge bg="danger" title={g.rejectionReason || 'Denegado'}>Denegado</Badge>;
  if (g.status === 'voided') return <Badge bg="secondary" title={g.voidReason || 'Anulado'}>Anulado</Badge>;
  return <Badge bg="success" title={g.verifiedBy ? `Verificado por ${g.verifiedBy}` : 'Recibido'}>Verificado</Badge>;
};

// An admin denying an ingreso can say why; whoever recorded it sees the reason next to it.
const DenyModal = ({ group, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setBusy(true); setError('');
    try { const { data } = await API.post(`/payments/${group.id}/reject`, { reason }); onDone(data.message); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo denegar el cobro.'); setBusy(false); }
  };
  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton><Modal.Title>Denegar ingreso</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="mb-2">{describe(group)}, registrado por {group.createdBy}. <AppliedTo g={group} /></p>
        <p className="small text-muted">No contará como recibido y las facturas seguirán igual.</p>
        <Form.Label className="small text-muted mb-1">Motivo <span className="text-muted">(opcional; lo verá quien lo registró)</span></Form.Label>
        <Form.Control as="textarea" rows={2} maxLength={300} autoFocus value={reason} onChange={e => setReason(e.target.value)} />
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button variant="danger" disabled={busy} onClick={submit}>{busy ? 'Denegando...' : 'Denegar ingreso'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Anular cobro: an admin reverses a verified ingreso entered by mistake — all of it. The amounts go back onto the invoices; the
// ingreso stays on record as voided, with who and why (the reason is required).
const VoidModal = ({ group, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ok = reason.trim().length >= 3;
  const submit = async () => {
    setBusy(true); setError('');
    try { const { data } = await API.post(`/payments/${group.id}/void`, { reason }); onDone(data.message); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo anular el cobro.'); setBusy(false); }
  };
  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton><Modal.Title>Anular ingreso</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="mb-2">{describe(group)}, registrado por {group.createdBy || '—'}. <AppliedTo g={group} /></p>
        <div className="alert alert-warning small py-2">
          Se anula <strong>todo el ingreso</strong>: cada monto <strong>vuelve a quedar pendiente</strong> en su factura y deja de contar como recibido en los reportes.
          {group.advance > 0 && <> El anticipo también deja de contar (no se puede anular si ya se usó para pagar facturas).</>} El ingreso se queda en el historial marcado como anulado, con tu nombre y el motivo.
        </div>
        <Form.Label className="small text-muted mb-1">Motivo de la anulación <span style={{ color: 'var(--mm-red)' }}>(obligatorio)</span></Form.Label>
        <Form.Control as="textarea" rows={2} maxLength={300} autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Ej.: se escribió mal el monto" />
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button variant="danger" disabled={busy || !ok} onClick={submit}>{busy ? 'Anulando...' : 'Anular ingreso'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

const IngresosPage = () => {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const [params] = useSearchParams();
  const wanted = params.get('tab');
  const [tab, setTab] = useState(TABS.includes(wanted) && (wanted !== 'verify' || isAdmin) ? wanted : 'pending');
  const [invoices, setInvoices] = useState(null);
  const [payments, setPayments] = useState(null); // history rows (every status)
  const [waiting, setWaiting] = useState(null); // rows por verificar (admins)
  const [credits, setCredits] = useState(null); // clients with saldo a favor
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [ingreso, setIngreso] = useState(null); // the new-ingreso dialog: { invoice?, client?, useCredit? }
  const [denying, setDenying] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [showAccounts, setShowAccounts] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const fail = (what) => (err) => setError(err.response?.data?.message || what);
  const loadPending = useCallback(() => API.get('/payments/pending-invoices').then(r => setInvoices(r.data)).catch(fail('No se pudieron cargar las facturas por cobrar.')), []);
  const loadHistory = useCallback(() => API.get('/payments?limit=300&transports=1').then(r => setPayments(r.data)).catch(fail('No se pudieron cargar los cobros.')), []);
  const loadWaiting = useCallback(() => API.get('/payments?status=pending&limit=500').then(r => setWaiting(r.data)).catch(fail('No se pudieron cargar los cobros por verificar.')), []);
  const loadCredits = useCallback(() => API.get('/payments/credits').then(r => setCredits(r.data)).catch(fail('No se pudieron cargar los anticipos.')), []);
  const loadSummary = useCallback(() => API.get('/payments/summary').then(r => setSummary(r.data)).catch(() => {}), []);
  const refreshAll = useCallback(() => {
    loadPending(); loadSummary(); loadCredits();
    if (isAdmin) loadWaiting();
    if (payments) loadHistory();
  }, [loadPending, loadSummary, loadCredits, loadWaiting, loadHistory, isAdmin, payments]);

  useEffect(() => { loadPending(); loadSummary(); loadCredits(); if (isAdmin) loadWaiting(); }, [loadPending, loadSummary, loadCredits, loadWaiting, isAdmin]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { setPage(1); }, [search, tab]);

  const term = norm(search.trim());
  const creditOf = useMemo(() => new Map((credits || []).map(c => [c.client._id, c.credit])), [credits]);
  const shownInvoices = useMemo(() => (invoices || []).filter(i => !term
    || [i.client?.name, i.client?.rnc, i.client?.cedula, i.number, i.ncf].some(v => norm(v).includes(term))), [invoices, term]);
  const matchGroup = useCallback((g) => !term
    || [g.client, g.accountName, g.createdBy, g.notes, g.transport ? 'transporte' : '', ...g.invoices.map(([n]) => n)].some(v => norm(v).includes(term)), [term]);
  const historyGroups = useMemo(() => groupPayments(payments || []), [payments]);
  const waitingGroups = useMemo(() => groupPayments(waiting || []), [waiting]);
  const shownHistory = useMemo(() => historyGroups.filter(matchGroup), [historyGroups, matchGroup]);
  const shownWaiting = useMemo(() => waitingGroups.filter(matchGroup), [waitingGroups, matchGroup]);
  const shownCredits = useMemo(() => (credits || []).filter(c => !term || [c.client.name, c.client.rnc, c.client.cedula].some(v => norm(v).includes(term))), [credits, term]);

  const totalOwed = (invoices || []).reduce((s, i) => s + i.balance, 0);
  const overdue = (invoices || []).filter(i => i.daysLate > 0);
  const overdueOwed = overdue.reduce((s, i) => s + i.balance, 0);
  const totalCredit = (credits || []).reduce((s, c) => s + c.credit, 0);

  const lists = { pending: shownInvoices, verify: shownWaiting, credits: shownCredits, history: shownHistory };
  const list = lists[tab] || [];
  const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const saved = (r) => {
    setIngreso(null);
    // Waiting for verification: say what was applied (the invoices haven't changed yet). Otherwise: where each invoice stands now.
    const numberOf = new Map(r.invoices.map(i => [i._id, i.number]));
    const applied = new Map();
    for (const p of r.payments.filter(x => x.kind !== 'advance')) applied.set(numberOf.get(p.invoice) || '?', (applied.get(numberOf.get(p.invoice) || '?') || 0) + p.amount);
    const parts = [
      ...(r.pendingVerification
        ? [...applied].map(([n, a]) => `${n} ${money(a)}`)
        : r.invoices.map(i => `${i.number}: ${i.status === 'paid' ? 'saldada' : `pendiente ${money(i.balance)}`}`)),
      ...(r.advance > 0 ? [`anticipo ${money(r.advance)}`] : []),
      ...(r.usedCredit > 0 ? [`${money(r.usedCredit)} pagados con saldo a favor`] : []),
    ];
    const what = r.cash > 0 ? `Ingreso de ${money(r.cash)}` : 'Uso de saldo a favor';
    setNotice(r.pendingVerification
      ? `${what} registrado (${parts.join(' · ')}). Queda POR VERIFICAR: no cuenta como recibido hasta que un administrador lo apruebe. ${r.emailed > 0 ? 'Ya se les avisó por correo.' : `${r.emailNote || 'No se pudo avisar por correo.'} Un administrador puede verificarlo en Ingresos.`}`
      : `${what} registrado: ${parts.join(' · ')}.`);
    refreshAll();
  };

  const approve = async (g) => {
    setBusyId(g.key); setError(''); setNotice('');
    try { const { data } = await API.post(`/payments/${g.id}/verify`); setNotice(data.message); refreshAll(); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo verificar el cobro.'); }
    finally { setBusyId(''); }
  };
  const denied = (message) => { setDenying(null); setNotice(message); refreshAll(); };
  const voided = (message) => { setVoiding(null); setNotice(message); refreshAll(); };

  const placeholder = tab === 'pending' ? 'Buscar por cliente o número de factura...'
    : tab === 'credits' ? 'Buscar por cliente...' : 'Buscar por cliente, factura, cuenta o quien lo registró...';
  const loading = tab === 'pending' ? invoices === null : tab === 'verify' ? waiting === null : tab === 'credits' ? credits === null : payments === null;
  const emptyText = term
    ? `Nada coincide con "${search.trim()}".`
    : tab === 'pending' ? 'No hay facturas con saldo pendiente. 🎉'
      : tab === 'verify' ? 'No hay cobros por verificar.'
        : tab === 'credits' ? 'Ningún cliente tiene saldo a favor.' : 'Todavía no se ha registrado ningún cobro.';
  const titles = { pending: 'Facturas con saldo pendiente', verify: 'Ingresos por verificar', credits: 'Clientes con saldo a favor', history: 'Últimos cobros' };
  const waitingCount = summary?.pending.count || 0;
  const tileMd = isAdmin ? 4 : 6;

  // Like Manifold Manager: nothing but the loading indicator until the page's data has come back.
  if (invoices === null && !error) return <Loading page label="Cargando ingresos..." />;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <h2 className="app-title mb-0" style={{ textAlign: 'left' }}>Ingresos</h2>
        <div className="d-flex gap-2">
          {isAdmin && <Button variant="outline-secondary" size="sm" onClick={() => setShowAccounts(true)}><i className="bi bi-bank me-1" />Cuentas bancarias</Button>}
          <Button variant="primary" disabled={invoices === null || credits === null} onClick={() => { setNotice(''); setIngreso({}); }}><i className="bi bi-plus-lg me-1" />Nuevo ingreso</Button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success d-flex justify-content-between align-items-start">
        <span>{notice}</span>
        <button type="button" className="close" aria-label="Cerrar" onClick={() => setNotice('')}><span aria-hidden="true">&times;</span></button>
      </div>}

      <Row className="mb-3">
        <Col md={tileMd} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Por cobrar</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-yellow)' }}>{money(totalOwed)}</h2>
            <div className="small text-muted mt-1">{invoices ? `${invoices.length} ${invoices.length === 1 ? 'factura' : 'facturas'} con saldo pendiente` : 'Cargando...'}</div>
          </Card>
        </Col>
        <Col md={tileMd} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Vencido</div>
            <h2 className="mt-2 mb-0" style={{ color: overdue.length ? 'var(--mm-red)' : undefined }}>{money(overdueOwed)}</h2>
            <div className="small text-muted mt-1">{invoices ? (overdue.length ? `${overdue.length} ${overdue.length === 1 ? 'factura vencida' : 'facturas vencidas'}` : 'Ninguna factura vencida') : 'Cargando...'}</div>
          </Card>
        </Col>
        {isAdmin && (
          <Col md={tileMd}>
            <Card className="shadow-sm p-3 dashboard-card dashboard-card-link h-100" role="button" onClick={() => setTab('verify')}>
              <div className="text-muted small">Ingresos por aprobar</div>
              <h2 className="mt-2 mb-0" style={{ color: waitingCount ? 'var(--mm-yellow)' : undefined }}>{money(summary?.pending.amount)}</h2>
              <div className="small text-muted mt-1">{summary ? (waitingCount ? `${waitingCount} ${waitingCount === 1 ? 'ingreso espera' : 'ingresos esperan'} tu verificación` : 'Nada por verificar') : 'Cargando...'}</div>
            </Card>
          </Col>
        )}
      </Row>

      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="pending"><i className="bi bi-hourglass-split me-1" />Por cobrar</Nav.Link></Nav.Item>
        {isAdmin && (
          <Nav.Item>
            <Nav.Link eventKey="verify"><i className="bi bi-patch-check me-1" />Por verificar{waitingCount > 0 && <Badge bg="warning" className="ms-1">{waitingCount}</Badge>}</Nav.Link>
          </Nav.Item>
        )}
        <Nav.Item>
          <Nav.Link eventKey="credits"><i className="bi bi-piggy-bank me-1" />Anticipos{credits && credits.length > 0 && <Badge bg="info" className="ms-1">{credits.length}</Badge>}</Nav.Link>
        </Nav.Item>
        <Nav.Item><Nav.Link eventKey="history"><i className="bi bi-cash-coin me-1" />Cobros registrados</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="transport"><i className="bi bi-truck me-1" />Transportes</Nav.Link></Nav.Item>
      </Nav>

      {tab === 'transport' ? <TransportesTab isAdmin={isAdmin} /> : (
      <Card className="shadow-sm p-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <Card.Title className="mb-0">
            {titles[tab]}{' '}
            <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>· {list.length}{tab === 'credits' && totalCredit > 0 ? ` · ${money(totalCredit)} en total` : ''}</span>
          </Card.Title>
          <Form.Control style={{ maxWidth: 400 }} placeholder={placeholder} value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading ? <Loading label="Cargando..." /> : (
          <div className="table-responsive">
            {tab === 'pending' && (
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Factura</th><th>Cliente</th><th>Emitida</th><th>Vence</th>
                    <th className="text-end">Total</th><th className="text-end">Pagado</th><th className="text-end">Pendiente</th><th />
                  </tr>
                </thead>
                <tbody>
                  {slice.map(i => (
                    <tr key={i._id}>
                      <td><strong>{i.number}</strong>{i.ncf && <div className="small text-muted">{i.ncf}</div>}</td>
                      <td>
                        {i.client?.name || '—'}{(i.client?.rnc || i.client?.cedula) && <div className="small text-muted">{i.client.rnc || i.client.cedula}</div>}
                        {creditOf.get(i.client?._id) > 0 && <div><Badge bg="info" className="fw-normal" title="Este cliente pagó por adelantado; se puede usar en su próximo ingreso">Saldo a favor {money(creditOf.get(i.client._id))}</Badge></div>}
                      </td>
                      <td className="small">{stamp(i.date)}</td>
                      <td className="small">{dueDay(i.dueDate)}{i.daysLate > 0 && <div><Badge bg="danger">{i.daysLate} {i.daysLate === 1 ? 'día' : 'días'} de atraso</Badge></div>}</td>
                      <td className="text-end">{money(i.total)}</td>
                      <td className="text-end">{i.amountPaid > 0 ? money(i.amountPaid) : '—'}</td>
                      <td className="text-end">
                        <strong>{money(i.balance)}</strong>
                        {i.pendingAmount > 0 && <div><Badge bg="warning" title="Cobros registrados que un administrador todavía no verifica">{money(i.pendingAmount)} por verificar</Badge></div>}
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="success" disabled={i.available <= 0}
                          title={i.available <= 0 ? 'Los cobros por verificar ya cubren todo el saldo' : undefined}
                          onClick={() => { setNotice(''); setIngreso({ invoice: i._id }); }}><i className="bi bi-cash-coin me-1" />Cobrar</Button>
                      </td>
                    </tr>
                  ))}
                  {slice.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">{emptyText}</td></tr>}
                </tbody>
              </Table>
            )}

            {tab === 'verify' && (
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr><th>Recibido</th><th>Cliente</th><th>Aplicado a</th><th className="text-end">Monto</th><th>Forma de pago</th><th>Registró</th><th>Nota</th><th /></tr>
                </thead>
                <tbody>
                  {slice.map(g => (
                    <tr key={g.key}>
                      <td className="small">{stampTime(g.date)}</td>
                      <td>{g.client}</td>
                      <td><AppliedTo g={g} /></td>
                      <td className="text-end"><strong style={{ color: 'var(--mm-yellow)' }}>{g.cash > 0 ? money(g.cash) : '—'}</strong></td>
                      <td>{g.method ? METHOD_LABEL[g.method] || g.method : '—'}{g.accountName && <div className="small text-muted">{g.accountName}</div>}</td>
                      <td className="small">{g.createdBy || '—'}<div className="text-muted">{stampTime(g.createdAt)}</div></td>
                      <td className="small text-muted">{g.notes || ''}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="success" className="me-2" disabled={busyId === g.key} onClick={() => approve(g)}><i className="bi bi-check-lg me-1" />Aprobar</Button>
                        <Button size="sm" variant="outline-danger" disabled={busyId === g.key} onClick={() => { setNotice(''); setDenying(g); }}><i className="bi bi-x-lg me-1" />Denegar</Button>
                      </td>
                    </tr>
                  ))}
                  {slice.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">{emptyText}</td></tr>}
                </tbody>
              </Table>
            )}

            {tab === 'credits' && (
              <Table hover className="align-middle mb-0">
                <thead><tr><th>Cliente</th><th>Desde</th><th className="text-end">Saldo a favor</th><th /></tr></thead>
                <tbody>
                  {slice.map(c => {
                    const owes = (invoices || []).some(i => i.client?._id === c.client._id && i.available > 0);
                    return (
                      <tr key={c.client._id}>
                        <td>{c.client.name}{(c.client.rnc || c.client.cedula) && <div className="small text-muted">{c.client.rnc || c.client.cedula}</div>}</td>
                        <td className="small">{stamp(c.since)}</td>
                        <td className="text-end"><strong style={{ color: 'var(--mm-cyan)' }}>{money(c.credit)}</strong></td>
                        <td className="text-end">
                          <Button size="sm" variant={owes ? 'primary' : 'outline-secondary'} disabled={!owes} title={owes ? undefined : 'Este cliente no tiene facturas por cobrar'}
                            onClick={() => { setNotice(''); setIngreso({ client: c.client._id, useCredit: true }); }}><i className="bi bi-arrow-right-circle me-1" />Aplicar a facturas</Button>
                        </td>
                      </tr>
                    );
                  })}
                  {slice.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-4">{emptyText}</td></tr>}
                </tbody>
              </Table>
            )}

            {tab === 'history' && (
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr><th>Fecha</th><th>Cliente</th><th>Aplicado a</th><th className="text-end">Monto</th><th>Forma de pago</th><th>Estado</th><th>Registró</th><th>Autorizado por</th><th>Nota</th><th /></tr>
                </thead>
                <tbody>
                  {slice.map(g => {
                    const dead = g.status === 'rejected' || g.status === 'voided';
                    return (
                      <tr key={g.key} style={dead ? { opacity: 0.7 } : undefined}>
                        <td className="small">{stampTime(g.date)}</td>
                        <td>{g.client}</td>
                        <td><AppliedTo g={g} /></td>
                        <td className="text-end">
                          <strong style={{ color: g.status === 'verified' ? 'var(--mm-green)' : g.status === 'pending' ? 'var(--mm-yellow)' : undefined, textDecoration: dead ? 'line-through' : undefined }}>{g.cash > 0 ? money(g.cash) : '—'}</strong>
                        </td>
                        <td>{g.method ? METHOD_LABEL[g.method] || g.method : '—'}{g.accountName && <div className="small text-muted">{g.accountName}</div>}</td>
                        <td>
                          <StatusBadge g={g} />
                          {g.status === 'rejected' && g.rejectionReason && <div className="small text-muted mt-1">“{g.rejectionReason}”</div>}
                          {g.status === 'rejected' && g.rejectedBy && <div className="small text-muted">por {g.rejectedBy}</div>}
                          {g.status === 'voided' && g.voidReason && <div className="small text-muted mt-1">“{g.voidReason}”</div>}
                          {g.status === 'voided' && g.voidedBy && <div className="small text-muted">por {g.voidedBy}</div>}
                        </td>
                        <td className="small">{g.createdBy || '—'}</td>
                        <td className="small">{g.verifiedBy || '—'}</td>
                        <td className="small text-muted">{g.notes || ''}</td>
                        <td className="text-end text-nowrap">
                          <Button size="sm" variant="outline-secondary" className="me-1" title={g.transport ? 'Recibo de transporte para el cliente' : 'Recibo de ingreso para el cliente'}
                            onClick={() => window.open(`/ingresos/${g.id}/print`, '_blank')}><i className="bi bi-printer me-1" />Recibo</Button>
                          {/* A transporte is edited or deleted in its own tab, not voided as an ingreso. */}
                          {isAdmin && g.status === 'verified' && !g.transport && (
                            <Button size="sm" variant="outline-danger" title="Anular este ingreso (se anotó por error)" onClick={() => { setNotice(''); setVoiding(g); }}><i className="bi bi-slash-circle me-1" />Anular</Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {slice.length === 0 && <tr><td colSpan={10} className="text-center text-muted py-4">{emptyText}</td></tr>}
                </tbody>
              </Table>
            )}
          </div>
        )}

        {pages > 1 && <div className="d-flex justify-content-center mt-3"><Pager page={current} pages={pages} onChange={setPage} /></div>}
      </Card>
      )}

      {ingreso && invoices && credits && <IngresoModal invoices={invoices} credits={credits} preset={ingreso} onClose={() => setIngreso(null)} onSaved={saved} />}
      {denying && <DenyModal group={denying} onClose={() => setDenying(null)} onDone={denied} />}
      {voiding && <VoidModal group={voiding} onClose={() => setVoiding(null)} onDone={voided} />}
      {showAccounts && isAdmin && <BankAccountsModal onClose={() => setShowAccounts(false)} />}
    </div>
  );
};

export default IngresosPage;
